import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';

interface Group {
  id: string;
  name: string;
  contactCount: number;
}

interface ImportGroupSelectorProps {
  workspaceId: string;
  value: {
    mode: 'none' | 'new' | 'existing';
    groupId?: string;
    groupName?: string;
  };
  onChange: (value: ImportGroupSelectorProps['value']) => void;
  suggestedName: string;
}

export function ImportGroupSelector({
  workspaceId,
  value,
  onChange,
  suggestedName,
}: ImportGroupSelectorProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;

    const fetchGroups = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('view_group_counts')
          .select('*')
          .eq('workspace_id', workspaceId);

        if (error) throw error;

        const formattedGroups = (data || []).map((group) => ({
          id: group.group_id,
          name: group.group_name,
          contactCount: group.contact_count || 0,
        }));

        setGroups(formattedGroups);
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [workspaceId]);

  const handleModeChange = (newMode: 'none' | 'new' | 'existing') => {
    if (newMode === 'new') {
      onChange({
        mode: 'new',
        groupName: suggestedName,
        groupId: undefined,
      });
    } else if (newMode === 'existing') {
      onChange({
        mode: 'existing',
        groupId: groups[0]?.id,
        groupName: undefined,
      });
    } else {
      onChange({
        mode: 'none',
        groupId: undefined,
        groupName: undefined,
      });
    }
  };

  const handleGroupNameChange = (name: string) => {
    onChange({
      ...value,
      groupName: name,
    });
  };

  const handleGroupIdChange = (groupId: string) => {
    onChange({
      ...value,
      groupId,
    });
  };

  return (
    <div className="border rounded-lg p-4 mt-4">
      <Label className="text-sm font-medium mb-3 block">
        Add to Group (optional)
      </Label>

      <RadioGroup
        value={value.mode}
        onValueChange={handleModeChange}
        className="space-y-3"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="none" id="group-none" />
          <Label htmlFor="group-none" className="font-normal cursor-pointer">
            Don't add to a group
          </Label>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="new" id="group-new" />
            <Label htmlFor="group-new" className="font-normal cursor-pointer">
              Create new group
            </Label>
          </div>
          {value.mode === 'new' && (
            <div className="ml-6">
              <Label htmlFor="group-name" className="text-xs text-muted-foreground">
                Group name:
              </Label>
              <Input
                id="group-name"
                value={value.groupName || ''}
                onChange={(e) => handleGroupNameChange(e.target.value)}
                placeholder="Enter group name"
                className="mt-1"
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="existing" id="group-existing" />
            <Label htmlFor="group-existing" className="font-normal cursor-pointer">
              Add to existing group
            </Label>
          </div>
          {value.mode === 'existing' && (
            <div className="ml-6">
              <Select
                value={value.groupId || ''}
                onValueChange={handleGroupIdChange}
                disabled={loading || groups.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      loading
                        ? 'Loading groups...'
                        : groups.length === 0
                        ? 'No groups available'
                        : 'Select a group...'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name} ({group.contactCount} contacts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </RadioGroup>
    </div>
  );
}
